import { UserRole } from '#enums';
import { UserDetails } from '#models';
import { inject, Injectable } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject, firstValueFrom, map } from 'rxjs';
import { AnalyticsService } from '../analytics.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _msalService = inject(MsalService);
  private _msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private _analyticsService = inject(AnalyticsService);

  private _userDetails$ = new BehaviorSubject<UserDetails | null>(null);

  get userDetails$() {
    return this._userDetails$.asObservable();
  }

  get isAdmin$() {
    return this.userDetails$.pipe(map(d => d?.isAdmin ?? false));
  }

  setUserDetails(userDetails: UserDetails | null) {
    this._userDetails$.next(userDetails);

    // Set user context in analytics when user details are available
    if (userDetails) {
      this._analyticsService.setUserContext(userDetails.email || this.getAccountEmail());
    } else {
      this._analyticsService.clearUserContext();
    }
  }

  getUserDetails() {
    return this._userDetails$.getValue();
  }

  isAuthorized(roles: UserRole[] = []): boolean {
    const userDetails = this._userDetails$.getValue();
    if (!userDetails) {
      return false;
    }

    if (roles.includes(UserRole.User)) {
      return userDetails.hasAccess;
    }

    if (roles.includes(UserRole.Admin)) {
      return userDetails.isAdmin;
    }

    return false;
  }
  isAuthenticated() {
    const accounts = this._msalService.instance.getAllAccounts();
    return accounts && accounts.length > 0;
  }

  getAccount() {
    const accounts = this._msalService.instance.getAllAccounts();
    return accounts && accounts.length > 0 ? accounts[0] : null;
  }

  getRoles() {
    const account = this.getAccount();
    if (!account) {
      return [];
    }

    const idTokenClaims = account.idTokenClaims as { roles?: string[] };
    return idTokenClaims.roles || [];
  }

  isAdmin(): boolean {
    const roles = this.getRoles();
    return roles.includes(UserRole.Admin);
  }

  getAccountEmail() {
    return this.getAccount()?.username || '';
  }

  initialize(): Promise<void> {
    return this._msalService.instance.initialize().then(async () => {
      // Handle redirect from login page
      const result = await this._msalService.instance.handleRedirectPromise();

      if (result !== null) {
        // User just completed login redirect
        this._msalService.instance.setActiveAccount(result.account);
        // Set user context in analytics after successful login
        this._analyticsService.setUserContext(result.account?.username || '');
        return;
      }

      // Check if user already has an active session
      if (this.isAuthenticated()) {
        const account = this.getAccount();
        this._msalService.instance.setActiveAccount(account);
        // Set user context in analytics for existing session
        if (account) {
          this._analyticsService.setUserContext(account.username || '');
        }
        return;
      }

      return firstValueFrom(this._msalService.loginRedirect(this._msalGuardConfig.authRequest as RedirectRequest));
    });
  }
}
